import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import { Grid, Paper, Tab, Box, Stack } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { getSmartkioskInitialValues } from "app/pages/customers/dsok/utils/getSmartkioskInitialValues";
import {
  BasicSettingsTab,
  DatavisionPOSTab,
  LicenseTab,
  OrderNotificationsTab,
  RelationsTab,
  ExternalOrdersTab,
  buildOrderNotificationsInitialValues,
} from "app/pages/customers/dsok/smartkioskFields/SmartkioskCustomerFields/tabs";
import { DSOK_CUSTOMER_FIELD_SCHEMA } from "app/pages/customers/dsok/smartkioskFields/SmartkioskCustomerFields";
import { mergeDeep } from "app/utils/objectHelpers";
import { getLocationsFieldSchema } from "app/pages/customers/dsok/smartkioskFields/SmartkioskLocationFields";
import { getDevicesFieldSchema } from "app/pages/customers/dsok/fields/DevicesField";
import merge from "lodash/merge";
import DsokCustomerDetailLayout from "../components/DsokCustomerDetailLayout";
import DsokCustomerDetailDirtySync from "../components/DsokCustomerDetailDirtySync";
import DsokCustomerDetailSectionRegister from "../components/DsokCustomerDetailSectionRegister";
import { useDsokCustomerDetail } from "../data/useDsokCustomerDetail";
import { updateDsokCustomerSettings } from "../data/dsokCustomerDetailApi";
import { sanitizeLogoUrlString } from "app/pages/customers/dsok/utils/sanitizeLogoUrlString";

function generateUuidV4() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const isBlankStringValue = (value) =>
  value == null || String(value).trim() === "";

const removeNestedWorkspaceDlaConnectionCode = (customerSlice) => {
  if (
    customerSlice?.workspaces &&
    typeof customerSlice.workspaces === "object" &&
    "dlaConnectionCode" in customerSlice.workspaces
  ) {
    const { dlaConnectionCode: _removed, ...workspaces } =
      customerSlice.workspaces;
    customerSlice.workspaces = workspaces;
  }
};

const DsokCustomerSettingsPage = () => {
  const { customerUrl } = useParams();
  const { t } = useTranslation();
  const auth = useJumboAuth();
  const authUser = auth?.getAuthUser?.() || {};
  const userTyp = authUser?.type ?? authUser?.typ;
  const isAdmin = userTyp === 1;
  const readOnly = !isAdmin;

  const {
    customer,
    originalDsokSettings,
    setOriginalDsokSettings,
    refetch,
  } = useDsokCustomerDetail();
  const [activeTab, setActiveTab] = useState("basicSettings");
  const generatedDlaConnectionCodeRef = useRef("");
  const persistedGeneratedDlaConnectionCodeRef = useRef("");

  const initialCustomerValues = useMemo(() => {
    if (!customer) {
      return {
        ...DSOK_CUSTOMER_FIELD_SCHEMA,
        locations: {},
        devices: {},
      };
    }
    const fullInitial = getSmartkioskInitialValues(customer);
    if (!fullInitial || !fullInitial.customer) {
      const dsokSettings = customer.dsokSettings || {};
      const merged = {
        ...mergeDeep(DSOK_CUSTOMER_FIELD_SCHEMA, dsokSettings.customer || {}),
        locations: merge(
          getLocationsFieldSchema(dsokSettings.customer?.locations),
          dsokSettings.customer?.locations || {}
        ),
        devices: merge(
          getDevicesFieldSchema(dsokSettings.customer?.devices),
          dsokSettings.customer?.devices || {}
        ),
      };
      if (isBlankStringValue(merged?.dlaConnectionCode)) {
        const generatedCode =
          generatedDlaConnectionCodeRef.current || generateUuidV4();
        generatedDlaConnectionCodeRef.current = generatedCode;
        merged.dlaConnectionCode = generatedCode;
      }
      removeNestedWorkspaceDlaConnectionCode(merged);
      if ("datavisionPosUsers" in merged) {
        delete merged.datavisionPosUsers;
      }
      return merged;
    }
    const cust = fullInitial.customer;
    if (isBlankStringValue(cust?.dlaConnectionCode)) {
      const generatedCode =
        generatedDlaConnectionCodeRef.current || generateUuidV4();
      generatedDlaConnectionCodeRef.current = generatedCode;
      cust.dlaConnectionCode = generatedCode;
    }
    removeNestedWorkspaceDlaConnectionCode(cust);
    if (cust && typeof cust === "object" && "datavisionPosUsers" in cust) {
      const { datavisionPosUsers: _omit, ...rest } = cust;
      return rest;
    }
    return cust;
  }, [customer]);

  useEffect(() => {
    const generatedCode = initialCustomerValues?.dlaConnectionCode;
    const storedCode = originalDsokSettings?.customer?.dlaConnectionCode;
    if (
      !isAdmin ||
      !customerUrl ||
      !originalDsokSettings ||
      isBlankStringValue(generatedCode) ||
      !isBlankStringValue(storedCode) ||
      persistedGeneratedDlaConnectionCodeRef.current === generatedCode
    ) {
      return;
    }

    let cancelled = false;
    persistedGeneratedDlaConnectionCodeRef.current = generatedCode;
    const persistGeneratedCode = async () => {
      const nextDsokSettings = JSON.parse(JSON.stringify(originalDsokSettings));
      removeNestedWorkspaceDlaConnectionCode(nextDsokSettings.customer);
      nextDsokSettings.customer = {
        ...(nextDsokSettings.customer || {}),
        dlaConnectionCode: generatedCode,
      };
      await updateDsokCustomerSettings(customerUrl, nextDsokSettings);
      if (cancelled) return;
      setOriginalDsokSettings(nextDsokSettings);
      refetch?.();
    };

    persistGeneratedCode().catch(() => {
      if (!cancelled) {
        persistedGeneratedDlaConnectionCodeRef.current = "";
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    customerUrl,
    initialCustomerValues?.dlaConnectionCode,
    isAdmin,
    originalDsokSettings,
    refetch,
    setOriginalDsokSettings,
  ]);

  const formInitialValues = {
    customer: initialCustomerValues,
    // Ukladá sa do settings.orderNotifications, teda mimo cesty tejto sekcie
    // (["customer"]). Odovzdáva sa preto v payloade ako _orderNotifications.
    orderNotifications: buildOrderNotificationsInitialValues(
      customer?.dsokSettings?.settings?.orderNotifications ??
        customer?.settings?.orderNotifications,
    ),
    pos: {
      ...(customer?.dsokSettings?.customer?.pos || {}),
      ...(customer?.dsokSettings?.pos || {}),
      productsPerRow:
        Number.isInteger(customer?.dsokSettings?.pos?.productsPerRow) &&
        customer?.dsokSettings?.pos?.productsPerRow > 0
          ? customer.dsokSettings.pos.productsPerRow
          : Number.isInteger(customer?.dsokSettings?.customer?.pos?.productsPerRow) &&
              customer?.dsokSettings?.customer?.pos?.productsPerRow > 0
            ? customer.dsokSettings.customer.pos.productsPerRow
          : 3,
    },
    productDetail: {
      ...(customer?.dsokSettings?.customer?.productDetail || {}),
      ...(customer?.dsokSettings?.productDetail || {}),
      bindingProductsPerRow:
        Number.isInteger(
          customer?.dsokSettings?.productDetail?.bindingProductsPerRow,
        ) && customer?.dsokSettings?.productDetail?.bindingProductsPerRow > 0
          ? customer.dsokSettings.productDetail.bindingProductsPerRow
          : Number.isInteger(
                customer?.dsokSettings?.customer?.productDetail
                  ?.bindingProductsPerRow,
              ) &&
              customer?.dsokSettings?.customer?.productDetail
                ?.bindingProductsPerRow > 0
            ? customer.dsokSettings.customer.productDetail.bindingProductsPerRow
          : 4,
      showBindingsInRow:
        customer?.dsokSettings?.productDetail?.showBindingsInRow !== undefined
          ? customer.dsokSettings.productDetail.showBindingsInRow === true
          : customer?.dsokSettings?.customer?.productDetail?.showBindingsInRow !==
                undefined
            ? customer.dsokSettings.customer.productDetail.showBindingsInRow ===
              true
          : true,
    },
  };

  return (
    <DsokCustomerDetailLayout
      lastBreadcrumbLabel={t("pages.dsokDetails.customerSettings")}
    >
      <Paper
        variant="outlined"
        sx={{
          bgcolor: "background.paper",
          p: 2,
          borderRadius: 1,
        }}
      >
        <Formik
          initialValues={formInitialValues}
          enableReinitialize
          onSubmit={() => {}}
        >
          <Form>
            <DsokCustomerDetailDirtySync />
            <DsokCustomerDetailSectionRegister
              sectionId="settings"
              getPayload={(values) => {
                const c = values?.customer;
                if (!c) return null;
                const {
                  datavisionPosUsers: _omitUsers,
                  enableDynamicComponents: _removed,
                  enableStatusScreen: _removed2,
                  ...rest
                } = c;
                const preservedPosUsers =
                  originalDsokSettings?.customer?.datavisionPosUsers;
                const productsPerRow = Number(values?.pos?.productsPerRow);
                const bindingProductsPerRow = Number(
                  values?.productDetail?.bindingProductsPerRow,
                );
                const showBindingsInRow =
                  values?.productDetail?.showBindingsInRow === true;

                const isProductsPerRowValid =
                  Number.isInteger(productsPerRow) && productsPerRow > 0;
                const isBindingProductsPerRowValid =
                  Number.isInteger(bindingProductsPerRow) &&
                  bindingProductsPerRow > 0;

                if (
                  values?.customer?.datavisionPosEnabled === true &&
                  (!isProductsPerRowValid || !isBindingProductsPerRowValid)
                ) {
                  return null;
                }

                return {
                  ...rest,
                  ...(typeof rest.mainLogo === "string"
                    ? { mainLogo: sanitizeLogoUrlString(rest.mainLogo) }
                    : {}),
                  ...(typeof rest.tableImage === "string"
                    ? { tableImage: sanitizeLogoUrlString(rest.tableImage) }
                    : {}),
                  ...(preservedPosUsers !== undefined && {
                    datavisionPosUsers: preservedPosUsers,
                  }),
                  // Keep a mirrored customer fallback in case backend strips new root keys.
                  pos: {
                    ...(rest?.pos || {}),
                    ...(values?.pos || {}),
                    productsPerRow: isProductsPerRowValid
                      ? productsPerRow
                      : 3,
                  },
                  productDetail: {
                    ...(rest?.productDetail || {}),
                    ...(values?.productDetail || {}),
                    bindingProductsPerRow: isBindingProductsPerRowValid
                      ? bindingProductsPerRow
                      : 4,
                    showBindingsInRow,
                  },
                  _dsokPos: {
                    ...(values?.pos || {}),
                    productsPerRow: isProductsPerRowValid
                      ? productsPerRow
                      : 3,
                  },
                  _dsokProductDetail: {
                    ...(values?.productDetail || {}),
                    bindingProductsPerRow: isBindingProductsPerRowValid
                      ? bindingProductsPerRow
                      : 4,
                    showBindingsInRow,
                  },
                  _orderNotifications: values?.orderNotifications,
                };
              }}
            />
            <TabContext value={activeTab}>
              <Stack sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "stretch",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      borderRight: 1,
                      borderColor: "divider",
                      minWidth: 220,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <TabList
                      orientation="vertical"
                      onChange={(_, newTab) => setActiveTab(newTab)}
                      aria-label={t(
                        "pages.dsokDetails.customerSettingsTabs.tabs.basicSettings",
                      )}
                      sx={{
                        flex: 1,
                        "& .MuiTabList-flexContainer": {
                          flexDirection: "column",
                          alignItems: "stretch",
                        },
                      }}
                    >
                      <Tab
                        label={t(
                          "pages.dsokDetails.customerSettingsTabs.tabs.basicSettings",
                        )}
                        value="basicSettings"
                        sx={{ textTransform: "none", alignItems: "flex-start" }}
                      />
                      <Tab
                        label={t(
                          "pages.dsokDetails.customerSettingsTabs.tabs.license",
                        )}
                        value="license"
                        sx={{ textTransform: "none", alignItems: "flex-start" }}
                      />
                      <Tab
                        label={t(
                          "pages.dsokDetails.customerSettingsTabs.tabs.relations",
                        )}
                        value="relations"
                        sx={{ textTransform: "none", alignItems: "flex-start" }}
                      />
                      <Tab
                        label={t(
                          "pages.dsokDetails.customerSettingsTabs.tabs.datavisionPos",
                        )}
                        value="datavisionPos"
                        sx={{ textTransform: "none", alignItems: "flex-start" }}
                      />
                      <Tab
                        label={t(
                          "dsokCustomerDetail.sections.orderNotifications.title",
                        )}
                        value="orderNotifications"
                        sx={{ textTransform: "none", alignItems: "flex-start" }}
                      />
                      <Tab
                        label={t(
                          "pages.dsokDetails.externalOrders.tabTitle",
                          { defaultValue: "Externé objednávky" },
                        )}
                        value="externalOrders"
                        sx={{ textTransform: "none", alignItems: "flex-start" }}
                      />
                    </TabList>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, pl: 2 }}>
                    <TabPanel value="basicSettings" sx={{ p: 0 }}>
                      <Grid container spacing={2}>
                        <BasicSettingsTab
                          isPartnerAdmin={false}
                          readOnly={readOnly}
                        />
                      </Grid>
                    </TabPanel>

                    <TabPanel value="license" sx={{ p: 0 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <LicenseTab readOnly={readOnly} />
                        </Grid>
                      </Grid>
                    </TabPanel>

                    <TabPanel value="relations" sx={{ p: 0 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <RelationsTab readOnly={readOnly} />
                        </Grid>
                      </Grid>
                    </TabPanel>

                    <TabPanel value="datavisionPos" sx={{ p: 0 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <DatavisionPOSTab readOnly={readOnly} />
                        </Grid>
                      </Grid>
                    </TabPanel>

                    <TabPanel value="orderNotifications" sx={{ p: 0 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <OrderNotificationsTab readOnly={readOnly} />
                        </Grid>
                      </Grid>
                    </TabPanel>

                    <TabPanel value="externalOrders" sx={{ p: 0 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <ExternalOrdersTab readOnly={readOnly} />
                        </Grid>
                      </Grid>
                    </TabPanel>
                  </Box>
                </Box>
              </Stack>
            </TabContext>
          </Form>
        </Formik>
      </Paper>
    </DsokCustomerDetailLayout>
  );
};

export default DsokCustomerSettingsPage;
